const WS_URL = process.env.REACT_APP_WS_URL || 'ws://localhost:8000';

export const MessageTypes = {
  USER_MESSAGE: 'user_message',
  REGENERATE: 'regenerate',
  PROGRESS: 'progress',
  TRANSLATION_COMPLETE: 'translation_complete',
  ERROR: 'error',
  CHAT_MESSAGE: 'chat_message',
};

export class WebSocketService {
  constructor() {
    this.socket = null;
    this.chatId = null;
    this.token = null;
    this.listeners = new Map();
    this.reconnectAttempts = 0;
    this.reconnectDelay = 1000;
    this.maxReconnectDelay = 30000;
    this.connectionId = 0;
    this.shouldReconnect = true;
  }

  connect(chatId, token) {
    // Close existing connection first
    if (this.socket) {
      this.shouldReconnect = false;
      this.socket.close(1000, 'New connection requested');
      this.socket = null;
    }

    this.shouldReconnect = true;
    this.connectionId++;
    const currentConnectionId = this.connectionId;

    return new Promise((resolve, reject) => {
      this.chatId = chatId;
      this.token = token;

      const url = `${WS_URL}/ws/chat/${chatId}?token=${token}`;
      this.socket = new WebSocket(url);

      this.socket.onopen = () => {
        if (this.connectionId !== currentConnectionId) {
          return; // Stale connection
        }
        console.log('WebSocket connected');
        this.reconnectAttempts = 0;
        resolve();
      };

      this.socket.onclose = (event) => {
        if (this.connectionId !== currentConnectionId) {
          return; // Stale connection
        }
        console.log('WebSocket closed:', event.code, event.reason);
        this.emit('close', event);

        // Don't reconnect for auth/access errors or intentional disconnect
        const noReconnectCodes = [1000, 4001, 4003, 4004];
        if (noReconnectCodes.includes(event.code) || !this.shouldReconnect) {
          console.log('WebSocket closed, not reconnecting');
          return;
        }

        this.reconnectAttempts++;
        const delay = Math.min(this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1), this.maxReconnectDelay);
        this.emit('reconnecting', { attempt: this.reconnectAttempts });
        setTimeout(() => {
          if (this.connectionId === currentConnectionId && this.shouldReconnect) {
            console.log(`Reconnecting... (attempt ${this.reconnectAttempts})`);
            this.connect(this.chatId, this.token).then(() => {
              this.emit('reconnected', {});
            }).catch(() => {});
          }
        }, delay);
      };

      this.socket.onerror = (error) => {
        if (this.connectionId !== currentConnectionId) {
          return; // Stale connection
        }
        console.error('WebSocket error:', error);
        // Don't emit 'error' here - it conflicts with MessageTypes.ERROR from server
        // Connection errors are handled by onclose with reconnect logic
        reject(error);
      };

      this.socket.onmessage = (event) => {
        if (this.connectionId !== currentConnectionId) {
          return; // Stale connection
        }
        try {
          const message = JSON.parse(event.data);
          this.emit(message.type, message.data);
        } catch (error) {
          console.error('Failed to parse message:', error);
        }
      };
    });
  }

  disconnect() {
    this.shouldReconnect = false;
    if (this.socket) {
      this.socket.close(1000, 'Client disconnect');
      this.socket = null;
    }
  }

  send(type, data) {
    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      this.socket.send(JSON.stringify({ type, data }));
    } else {
      console.error('WebSocket is not connected');
    }
  }

  sendMessage(message) {
    this.send(MessageTypes.USER_MESSAGE, { message });
  }

  regenerate(instructions = '') {
    this.send(MessageTypes.REGENERATE, { instructions });
  }

  on(event, callback) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event).add(callback);
    return () => this.off(event, callback);
  }

  off(event, callback) {
    if (this.listeners.has(event)) {
      this.listeners.get(event).delete(callback);
    }
  }

  emit(event, data) {
    if (this.listeners.has(event)) {
      this.listeners.get(event).forEach((callback) => callback(data));
    }
  }

  isConnected() {
    return this.socket && this.socket.readyState === WebSocket.OPEN;
  }
}

export const wsService = new WebSocketService();
